import { AlertTriangle } from "lucide-react";

import { ColocaBadge } from "@/shared/components/data-display/ColocaBadge";
import { useTranslations } from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";

import type { AssignedRole } from "../../types/access.types";
import { roleColor } from "../../utils/roleColor";

/** Max pills rendered inline before the rest collapses into a "+N" chip. */
const MAX_VISIBLE_ROLES = 1;

/** Shared look of the "+N" chip — the clickable and static variants must stay visually identical. */
const CHIP_CLASS =
  "inline-flex items-center rounded-full border border-border bg-card px-2 py-0.5 font-mono text-[11px] font-semibold text-muted-foreground";

export interface PrincipalRolesCellProps {
  /** The principal's assigned roles, in backend order. */
  roles: AssignedRole[];
  /** The per-principal roles fetch failed — access is unknown, not confirmed as none. */
  rolesFailed: boolean;
  /**
   * Opens the principal's assign-roles drawer (the same one as the row's "Roles" button), where the full role
   * list lives. Omit when no drawer can be offered — either the surface has none (Simulator) or the roles
   * catalog 403'd — and the "+N" chip degrades to a non-interactive count. On management surfaces always pass
   * it when the catalog is available; leaving it out there would hide the only path to the full role list.
   */
  onShowAll?: () => void;
}

/**
 * "Assigned roles" cell shared by the principal tables (Usuarios, API Keys). With the granular backend
 * catalog a principal can hold dozens of roles, and rendering them all exploded the row height — so the cell
 * shows only the first pill and collapses the rest into a "+N" chip that opens the drawer. Display-only:
 * assignment always goes through the drawer, so capping what is painted here cannot affect the
 * effective-access resolution or the role mutations.
 */
export function PrincipalRolesCell({ roles, rolesFailed, onShowAll }: PrincipalRolesCellProps) {
  const { t } = useTranslations("settings");

  if (rolesFailed) {
    return (
      <div className="flex flex-wrap gap-1">
        <ColocaBadge variant="secondary" icon={AlertTriangle}>
          {t("access_badge_roles_unavailable")}
        </ColocaBadge>
      </div>
    );
  }

  if (roles.length === 0) {
    return (
      <div className="flex flex-wrap gap-1">
        <ColocaBadge variant="warning">{t("access_badge_no_roles")}</ColocaBadge>
      </div>
    );
  }

  const visible = roles.slice(0, MAX_VISIBLE_ROLES);
  const hiddenCount = roles.length - visible.length;

  return (
    <div className="flex flex-wrap gap-1">
      {visible.map((role) => (
        <span
          key={role.id}
          className="inline-flex items-center gap-1.5 rounded-full border border-border bg-muted/60 px-2 py-0.5 text-[11px]"
        >
          <span
            className="h-1.5 w-1.5 rounded-full"
            style={{ background: roleColor(role.slug) }}
            aria-hidden
          />
          {role.name}
        </span>
      ))}
      {hiddenCount > 0 &&
        (onShowAll ? (
          <button
            type="button"
            onClick={onShowAll}
            title={t("access_roles_overflow_action", { count: hiddenCount })}
            aria-label={t("access_roles_overflow_action", { count: hiddenCount })}
            className={cn(CHIP_CLASS, "transition-colors hover:bg-muted hover:text-foreground")}
          >
            +{hiddenCount}
          </button>
        ) : (
          <span title={t("access_roles_overflow_more", { count: hiddenCount })} className={CHIP_CLASS}>
            +{hiddenCount}
          </span>
        ))}
    </div>
  );
}
